import React, { FC, useCallback, useEffect, useState } from "react";
import GeometryTypeSelect, { GeometryType } from "./GeometryTypeSelect";
import _ from "lodash";
import { useMap } from "../../../App";
import ol from "openlayers";
import { InventoryGeometryItem } from "../../../hooks/database/types/inventories";
import { useBaseGeometryController } from "./useBaseGeometryController";

const CreateGeometry: FC<{
  onCancel: () => void;
  onSave: (geojson: InventoryGeometryItem["geom"]) => void;
}> = (props) => {
  const { drawSource, saveGeometry } = useBaseGeometryController(props.onSave);

  const [selectedGeomType, setSelectedGeomType] = useState<GeometryType>("Point");
  const handleGeomChange = useCallback(
    (value: GeometryType) => {
      setSelectedGeomType(value);
      setCoordinates([]);
      drawSource.clear();
    },
    [drawSource],
  );

  const [coordinates, setCoordinates] = useState<ol.Coordinate[]>([]);
  // note that for Point we don't store anything in `coordinates`, so it's always a viable geometry
  const isViableGeometry =
    selectedGeomType === "Point" ? true : selectedGeomType === "LineString" ? coordinates.length >= 2 : coordinates.length >= 3;
  // update the feature geometry in drawSourceRef based on the geometry type and coordinate array
  useEffect(() => {
    if (selectedGeomType === "Point") {
      drawSource.clear();
      return;
    }
    const geometry = selectedGeomType === "LineString" ? new ol.geom.LineString(coordinates) : new ol.geom.Polygon([coordinates]);
    if (drawSource.getFeatures().length === 0) {
      drawSource.addFeature(new ol.Feature(geometry));
    } else {
      drawSource.getFeatures()[0].setGeometry(geometry);
    }
  }, [selectedGeomType, coordinates, isViableGeometry, drawSource]);

  const map = useMap()!;
  const handleConfirmClick = useCallback(() => {
    const center = map.getView().getCenter();
    if (selectedGeomType === "Point") {
      saveGeometry(new ol.geom.Point(center));
    } else if (isViableGeometry && _.isEqual(center, coordinates[coordinates.length - 1])) {
      // If last point is the same as the current one end drawing and save.
      saveGeometry(
        selectedGeomType === "LineString" ? new ol.geom.LineString(coordinates) : new ol.geom.Polygon([[...coordinates, coordinates[0]]]),
      );
    } else {
      setCoordinates([...coordinates, center]);
    }
  }, [coordinates, isViableGeometry, map, saveGeometry, selectedGeomType]);

  return (
    <>
      <i className="fa fa-crosshairs draw-crosshair" />
      <GeometryTypeSelect required={true} value={selectedGeomType} onChange={handleGeomChange} />

      <button className="draw-confirm button button-fill" onClick={handleConfirmClick}>
        {selectedGeomType === "Point" ? "CONFIRM POSITION" : "CONFIRM GEOMETRY"}
      </button>

      <button className="draw-cancel button button-fill" onClick={props.onCancel}>
        CANCEL
      </button>
    </>
  );
};

export default CreateGeometry;
